import React, {
  createContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import api from "../services/api";
import storage from "../services/storage";
import encryption from "../services/encryption";

const AuthContext = createContext();

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  user: {
    uuid: null,
    sessionToken: null,
    keyVersion: null,
    publicKey: null,
  },
  jwtToken: null,
  refreshToken: null,
  twoFAEnabled: false,
  pinEnabled: false,
  recoveryCodesShown: false,
  error: null,
  authMode: "login",
  require2FA: false,
  requirePin: false,
  twoFACode: "",
  loginPassword: "",
  signupPassword: "",
  tokenInput: "",
  signupResult: null,
  pendingLoginData: null,
};

const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_AUTHENTICATED: "SET_AUTHENTICATED",
  SET_UNAUTHENTICATED: "SET_UNAUTHENTICATED",
  SET_USER: "SET_USER",
  SET_TOKENS: "SET_TOKENS",
  SET_TWOFA_ENABLED: "SET_TWOFA_ENABLED",
  SET_PIN_ENABLED: "SET_PIN_ENABLED",
  SET_RECOVERY_CODES_SHOWN: "SET_RECOVERY_CODES_SHOWN",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_AUTH_MODE: "SET_AUTH_MODE",
  SET_REQUIRE_2FA: "SET_REQUIRE_2FA",
  SET_REQUIRE_PIN: "SET_REQUIRE_PIN",
  SET_TWOFA_CODE: "SET_TWOFA_CODE",
  SET_LOGIN_PASSWORD: "SET_LOGIN_PASSWORD",
  SET_SIGNUP_PASSWORD: "SET_SIGNUP_PASSWORD",
  SET_TOKEN_INPUT: "SET_TOKEN_INPUT",
  SET_SIGNUP_RESULT: "SET_SIGNUP_RESULT",
  CLEAR_SIGNUP_RESULT: "CLEAR_SIGNUP_RESULT",
  SET_PENDING_LOGIN_DATA: "SET_PENDING_LOGIN_DATA",
  CLEAR_PENDING_LOGIN_DATA: "CLEAR_PENDING_LOGIN_DATA",
};

function authReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };


    case ACTIONS.SET_USER:
      return { 
        ...state, 
        user: { ...state.user, ...action.payload } 
      };

    case ACTIONS.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: true, error: null };

    case ACTIONS.SET_UNAUTHENTICATED:
      return {
        ...state,
        isAuthenticated: false,
        user: initialState.user,
        jwtToken: null,
        refreshToken: null,
      };

    case ACTIONS.SET_USER:
      return { ...state, user: { ...state.user, ...action.payload } };

    case ACTIONS.SET_TOKENS:
      return {
        ...state,
        jwtToken: action.payload.jwtToken,
        refreshToken: action.payload.refreshToken,
      };

    case ACTIONS.SET_TWOFA_ENABLED:
      return { ...state, twoFAEnabled: action.payload };

    case ACTIONS.SET_PIN_ENABLED:
      return { ...state, pinEnabled: action.payload };

    case ACTIONS.SET_RECOVERY_CODES_SHOWN:
      return { ...state, recoveryCodesShown: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case ACTIONS.SET_AUTH_MODE:
      return { ...state, authMode: action.payload };

    case ACTIONS.SET_REQUIRE_2FA:
      return { ...state, require2FA: action.payload };

    case ACTIONS.SET_REQUIRE_PIN:
      return { ...state, requirePin: action.payload };

    case ACTIONS.SET_TWOFA_CODE:
      return { ...state, twoFACode: action.payload };

    case ACTIONS.SET_LOGIN_PASSWORD:
      return { ...state, loginPassword: action.payload };

    case ACTIONS.SET_SIGNUP_PASSWORD:
      return { ...state, signupPassword: action.payload };

    case ACTIONS.SET_TOKEN_INPUT:
      return { ...state, tokenInput: action.payload };

    case ACTIONS.SET_SIGNUP_RESULT:
      return { ...state, signupResult: action.payload };

    case ACTIONS.CLEAR_SIGNUP_RESULT:
      return { ...state, signupResult: null };

    case ACTIONS.SET_PENDING_LOGIN_DATA:
      return { ...state, pendingLoginData: action.payload };

    case ACTIONS.CLEAR_PENDING_LOGIN_DATA:
      return { ...state, pendingLoginData: null };

    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load stored session on mount
useEffect(() => {
  const storedSession = storage.getSession();
  if (storedSession && !state.isAuthenticated) {
    console.log("🔵 [AuthContext] Loading stored session:", {
      hasPublicKey: !!storedSession.publicKey,
      uuid: storedSession.uuid
    });
    
    // Load public key from stored session
    if (storedSession.publicKey) {
      encryption.setPublicKey(storedSession.publicKey);
      console.log("🟢 [AuthContext] Public key loaded from stored session");
    } else {
      console.warn("🟡 [AuthContext] No public key in stored session");
    }
    
    dispatch({
      type: ACTIONS.SET_USER,
      payload: {
        uuid: storedSession.uuid,
        sessionToken: storedSession.sessionToken,
        keyVersion: storedSession.keyVersion,
        publicKey: storedSession.publicKey, // ← ADD THIS
      },
    });
    
    dispatch({
      type: ACTIONS.SET_TOKENS,
      payload: {
        jwtToken: storedSession.jwtToken,
        refreshToken: storedSession.refreshToken,
      },
    });
    
    dispatch({
      type: ACTIONS.SET_TWOFA_ENABLED,
      payload: storedSession.twoFAEnabled,
    });
    
    dispatch({
      type: ACTIONS.SET_PIN_ENABLED,
      payload: storedSession.pinEnabled,
    });
    
    dispatch({
      type: ACTIONS.SET_RECOVERY_CODES_SHOWN,
      payload: storedSession.recoveryCodesShown,
    });
    
    dispatch({ type: ACTIONS.SET_AUTHENTICATED });

    const privateKey = storage.getPrivateKey(storedSession.uuid);
    if (privateKey) {
      encryption.setPrivateKey(privateKey);
      console.log("🟢 [AuthContext] Private key loaded from storage");
    }
  }
}, [state.isAuthenticated]);

  // Complete login function
  const completeLogin = useCallback((data) => {
    dispatch({
      type: ACTIONS.SET_USER,
      payload: {
        uuid: data.uuid,
        sessionToken: data.sessionToken,
        keyVersion: data.keyVersion,
        publicKey: data.publicKey,
      },
    });
    dispatch({
      type: ACTIONS.SET_TOKENS,
      payload: {
        jwtToken: data.jwtToken,
        refreshToken: data.refreshToken,
      },
    });
    dispatch({ type: ACTIONS.SET_TWOFA_ENABLED, payload: data.twoFAEnabled });
    dispatch({ type: ACTIONS.SET_PIN_ENABLED, payload: data.pinEnabled });
    dispatch({
      type: ACTIONS.SET_RECOVERY_CODES_SHOWN,
      payload: data.recoveryCodesShown,
    });
    dispatch({ type: ACTIONS.SET_AUTHENTICATED });
    dispatch({ type: ACTIONS.SET_REQUIRE_2FA, payload: false });
    dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: false });
    dispatch({ type: ACTIONS.SET_LOGIN_PASSWORD, payload: "" });
    dispatch({ type: ACTIONS.SET_TWOFA_CODE, payload: "" });
    dispatch({ type: ACTIONS.SET_TOKEN_INPUT, payload: "" });
    dispatch({ type: ACTIONS.CLEAR_PENDING_LOGIN_DATA });

    storage.saveSession({
      jwtToken: data.jwtToken,
      refreshToken: data.refreshToken,
      uuid: data.uuid,
      sessionToken: data.sessionToken,
      keyVersion: data.keyVersion,
      twoFAEnabled: data.twoFAEnabled,
      pinEnabled: data.pinEnabled,
      recoveryCodesShown: data.recoveryCodesShown,
      publicKey: data.publicKey,
    });

    const privateKey = storage.getPrivateKey(data.uuid);
    if (privateKey) {
      encryption.setPrivateKey(privateKey);
    }

    return { success: true, sessionToken: data.sessionToken };
  }, []);

  // Login function
  const login = useCallback(
    async (pinCode = null) => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.CLEAR_ERROR });

      try {
        // If we have a PIN code, include it in the login request
        const data = await api.login(
          state.tokenInput,
          state.loginPassword,
          state.require2FA ? state.twoFACode : null,
          pinCode, // Pass PIN if provided
        );

        console.log("Login response data:", data);

        // Handle PIN requirement (first attempt without PIN)
        if (data.requirePin && !pinCode) {
          console.log("PIN required for login, storing credentials for retry");
          // Store that we need PIN and keep credentials
          dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: true });
          dispatch({ type: ACTIONS.SET_LOADING, payload: false });

          // Open PIN modal for verification
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("openPinModalForVerification"),
            );
          }, 100);

          return { success: false, requirePin: true };
        }

        // Handle 2FA requirement
        if (data.require2FA) {
          dispatch({ type: ACTIONS.SET_REQUIRE_2FA, payload: true });
          dispatch({ type: ACTIONS.SET_LOADING, payload: false });
          return { success: false, require2FA: true };
        }

        // If no 2FA or PIN required, complete login normally
        const result = completeLogin(data);
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
        return result;
      } catch (error) {
        console.error("Login error:", error);
        dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
        return { success: false, error: error.message };
      }
    },
    [
      state.tokenInput,
      state.loginPassword,
      state.require2FA,
      state.twoFACode,
      completeLogin,
    ],
  );

  const retryLoginWithPin = useCallback(
    async (pinCode) => {
      console.log("Retrying login with PIN:", pinCode);
      return await login(pinCode);
    },
    [login],
  );


  const refreshPublicKey = useCallback(async () => {
  if (!state.user.uuid) {
    console.error("Cannot refresh public key: No user UUID");
    return false;
  }
  
  try {
    console.log("🔵 [AuthContext] Refreshing public key from backend...");
    const response = await api.get(`/users/${state.user.uuid}/public-key`);
    
    if (response.publicKey) {
      encryption.setPublicKey(response.publicKey);
      dispatch({
        type: ACTIONS.SET_USER,
        payload: { publicKey: response.publicKey }
      });
      
      // Update stored session
      const session = storage.getSession();
      if (session) {
        session.publicKey = response.publicKey;
        storage.saveSession(session);
      }
      
      console.log("🟢 [AuthContext] Public key refreshed successfully");
      return true;
    } else {
      console.warn("🟡 [AuthContext] No public key in backend response");
      return false;
    }
  } catch (error) {
    console.error("🔴 [AuthContext] Failed to refresh public key:", error);
    return false;
  }
}, [state.user.uuid]);


  // Signup function
  const signup = useCallback(async (password) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: ACTIONS.CLEAR_ERROR });

    try {
      const { privateKey, publicKey } = encryption.generateKeyPair();

      const data = await api.signup(password, publicKey);

      storage.savePrivateKey(data.uuid, privateKey);
      encryption.setPrivateKey(privateKey);

      const signupData = {
        uuid: data.uuid,
        token: data.token,
        privateKey: privateKey,
        recoveryCodes: data.recoveryCodes,
      };

      dispatch({ type: ACTIONS.SET_SIGNUP_RESULT, payload: signupData });

      dispatch({
        type: ACTIONS.SET_USER,
        payload: {
          uuid: data.uuid,
          sessionToken: data.token,
          keyVersion: data.keyVersion,
        },
      });
      dispatch({ type: ACTIONS.SET_RECOVERY_CODES_SHOWN, payload: false });
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });

      return {
        success: true,
        ...signupData,
      };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: error.message };
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      if (state.refreshToken) {
        await api.logout(state.refreshToken);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      storage.clearSession();
      storage.clearAllPrivateKeys();
      encryption.setPrivateKey(null);
      dispatch({ type: ACTIONS.SET_UNAUTHENTICATED });
      dispatch({
        type: ACTIONS.SET_TOKENS,
        payload: { jwtToken: null, refreshToken: null },
      });
    }
  }, [state.refreshToken]);

  // Clear signup result
  const clearSignupResult = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_SIGNUP_RESULT });
  }, []);

  // Update 2FA status
  const updateTwoFAStatus = useCallback((enabled) => {
    dispatch({ type: ACTIONS.SET_TWOFA_ENABLED, payload: enabled });
    const session = storage.getSession();
    if (session) {
      session.twoFAEnabled = enabled;
      storage.saveSession(session);
    }
  }, []);

  // Update PIN status
  const updatePinStatus = useCallback((enabled) => {
    dispatch({ type: ACTIONS.SET_PIN_ENABLED, payload: enabled });
    const session = storage.getSession();
    if (session) {
      session.pinEnabled = enabled;
      storage.saveSession(session);
    }
  }, []);

  // Update recovery codes shown status
  const updateRecoveryCodesShown = useCallback((shown) => {
    dispatch({ type: ACTIONS.SET_RECOVERY_CODES_SHOWN, payload: shown });
    const session = storage.getSession();
    if (session) {
      session.recoveryCodesShown = shown;
      storage.saveSession(session);
    }
  }, []);

  // Logout event listener
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };

    window.addEventListener("logout", handleLogout);

    return () => {
      window.removeEventListener("logout", handleLogout);
    };
  }, [logout]);

  const value = {
    ...state,
    signup,
    login,
    retryLoginWithPin,
    logout,
    clearSignupResult,
    updateTwoFAStatus,
    updatePinStatus,
    updateRecoveryCodesShown,
    refreshPublicKey,
    dispatch,
    ACTIONS,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
